// A staff-run station's countdown screen, linked from its modal (ADR-0033, spec/13 §3.3): "Show countdown screen" makes
// a revocable, read-only link for a TV or tablet beside the station, shown once as a QR and a short URL. The link can be
// replaced ("New link": the old one stops) or unlinked. Nothing here reads the token back — the server keeps only its
// hash — so to show the QR again staff make a new link. `stations:manage`, checked again by the server.

import type { CreatedStationDisplay, FloorStationState } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, encodeQr, qrPath, useToast } from '@clavian/ui-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { Icon } from '../shell/nav-icons';
import { useCurrentVenue } from '../shell/scope';
import { Notice } from '../ui';
import { floorKeys } from './queries';
import { timeOfDay } from './live';
import { useProblem } from '../api/use-problem';

/** The link's status, never its token (`GET …/stations/{s}/display`). */
export function useStationDisplay(organizationId: string, venueId: string, stationId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: [...floorKeys.snapshot(organizationId, venueId), 'display', stationId] as const,
    queryFn: () => api.getStationDisplay({ path: { organizationId, venueId, stationId } }),
    enabled,
  });
}

export function CountdownScreenPanel({ station, actions }: { readonly station: FloorStationState; readonly actions?: ReactNode }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const api = useApi();
  const venue = useCurrentVenue();
  const queryClient = useQueryClient();
  const path = { organizationId: venue.organizationId, venueId: venue.id, stationId: station.stationId };
  const status = useStationDisplay(venue.organizationId, venue.id, station.stationId, true);
  // The link just made: its token is shown this once, and forgotten when the panel closes.
  const [created, setCreated] = useState<CreatedStationDisplay | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: [...floorKeys.snapshot(venue.organizationId, venue.id), 'display', station.stationId] });

  const link = useMutation({
    mutationFn: () => api.createStationDisplay({ path }),
    onSuccess: (body) => {
      setCreated(body);
      refresh();
    },
    onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('floor.drawer.screen.failed') }) }),
  });
  const revoke = useMutation({
    mutationFn: () => api.revokeStationDisplay({ path }),
    onSuccess: () => {
      setCreated(null);
      refresh();
      show({ tone: 'ok', title: t('floor.drawer.screen.revoked') });
    },
    onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('floor.drawer.screen.failed') }) }),
  });

  const live = status.data?.link ?? null;
  const copy = () => {
    if (created === null) return;
    void navigator.clipboard?.writeText(created.url).then(
      () => show({ tone: 'ok', title: t('floor.drawer.screen.copied') }),
      () => undefined,
    );
  };

  return (
    <div className="console-screen-link" aria-label={t('floor.drawer.screen.title')} role="group">
      <div className="console-screen-link__head">
        <p className="console-session__form-title">{t('floor.drawer.screen.title')}</p>
        {actions}
      </div>
      <p className="console-muted">{t('floor.drawer.screen.intro')}</p>

      {created !== null ? (
        <div className="console-screen-link__share">
          <ScreenQr url={created.url} />
          <div className="console-screen-link__words">
            <p>{t('floor.drawer.screen.scan')}</p>
            <p className="console-screen-link__url console-num" data-testid="display-url">
              {created.url}
            </p>
            <Button variant="secondary" size="sm" icon={<Icon name="layers" />} onClick={copy}>
              {t('floor.drawer.screen.copy')}
            </Button>
            <Notice tone="info">{t('floor.drawer.screen.once')}</Notice>
          </div>
        </div>
      ) : live !== null ? (
        <p className="console-screen-link__status">
          <span>{t('floor.drawer.screen.linkedSince', { time: timeOfDay(live.createdAt) ?? '' })}</span>
          <span className="console-muted">
            {live.lastSeenAt === null ? t('floor.drawer.screen.neverSeen') : t('floor.drawer.screen.lastSeen', { time: timeOfDay(live.lastSeenAt) ?? '' })}
          </span>
        </p>
      ) : (
        <p className="console-muted">{t('floor.drawer.screen.none')}</p>
      )}

      <div className="console-form__actions">
        {live === null && created === null ? (
          <Button variant="primary" size="sm" icon={<Icon name="qr" />} busy={link.isPending} onClick={() => link.mutate()}>
            {t('floor.drawer.screen.show')}
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" icon={<Icon name="refresh" />} busy={link.isPending} onClick={() => link.mutate()}>
              {t('floor.drawer.screen.rotate')}
            </Button>
            <Button variant="ghost" size="sm" busy={revoke.isPending} onClick={() => revoke.mutate()}>
              {t('floor.drawer.screen.revoke')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/** The link as a QR (the kit's dependency-free encoder), dark on white so any camera reads it. */
function ScreenQr({ url }: { readonly url: string }): ReactNode {
  const { t } = useTranslation('console');
  const code = useMemo(() => {
    try {
      return encodeQr(url);
    } catch {
      return null;
    }
  }, [url]);
  if (code === null) return null;
  return (
    <span className="console-screen-link__qr" role="img" aria-label={t('floor.drawer.screen.qrLabel')}>
      <svg viewBox={`-4 -4 ${code.size + 8} ${code.size + 8}`} shapeRendering="crispEdges" aria-hidden="true" focusable="false">
        <rect x="-4" y="-4" width={code.size + 8} height={code.size + 8} fill="#fff" />
        <path d={qrPath(code)} fill="#000" />
      </svg>
    </span>
  );
}
